import { Metadata } from '@fullstory/library-ui';
import {
  ElementPropertyRule,
  ElementPropertyRuleRecord,
  NamedElement,
  NamedElementRecord,
  Suggestion,
} from '@fullstory/proto/fs/services/search-settings';
import {
  DependentEventFilter,
  PlatformSelector,
  NativePlatform,
} from '@fullstory/proto/fs/api/segment';
import { int64 } from '@fullstory/proto/google/protobuf';
import { intl } from '@fullstory/intl';
import { cloneDeep } from 'lodash';
import * as vars from '@fullstory/proto/fs/api/vars';
import {
  ElementPropertyRuleConfig,
  FlattenedElementPropertyRule,
  NamedElementConfig,
  NamedElementEditableDetails,
} from '../types';
import { isNonNull } from '@fullstory/typeutils';
import { AuditMetadata, Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { GetNamedElementDetailViewResponse } from '@fullstory/proto/fs/services/dx-objects';
import { SelectorRule } from '@fullstory/selectors';
import { SelectorPart, SelectorPseudoClass } from '@fullstory/proto/fs/selectors/selectors.proto';

export * from './regex';
export * from './property-rules';

const SUPPORTED_OPERATORS = ['=', '|=', '^='];

export const placeholderNamedElementRecord = (
  initialElement: Partial<NamedElement> = {},
  webOnly: boolean = false,
): NamedElementRecord => {
  const namedElement: NamedElement = {
    id: '',
    name: '',
    description: '',
    apiName: '',
    platformSelectors: [{ selector: '', platforms: [] }],
    suggestion: null,
    ...initialElement,
  };
  if (webOnly) {
    namedElement.platformSelectors.forEach(ps => {
      if (!ps.platforms.length) {
        ps.platforms = [NativePlatform.NATIVE_PLATFORM_WEB];
      }
    });
  }
  return {
    namedElement,
    metadata: {
      lastUpdated: null,
      lastUpdatedBy: '',
      created: null,
      createdBy: '',
      archived: false,
      visibility: Visibility.UNSET,
      version: int64.nil,
      sequentialVersion: int64.nil,
    },
    createIdempotenceKey: '',
  };
};

export const duplicateNamedElementRecord = (record: NamedElementRecord): NamedElementRecord => ({
  namedElement: {
    id: '',
    name: intl.formatMessage(
      {
        defaultMessage: '{elementName} (copy)',
        id: 'u7zf9C',
        description: 'This is the default title of a duplicated row in a table',
      },
      { elementName: record.namedElement?.name },
    ),
    description: record.namedElement?.description || '',
    apiName: '',
    platformSelectors: cloneDeep(record.namedElement?.platformSelectors) || [],
    suggestion: null,
  },
  metadata: null,
  createIdempotenceKey: '',
});

export const namedElementEditableDetails = (
  details: Omit<GetNamedElementDetailViewResponse.Details, 'issues'> &
    Partial<Pick<GetNamedElementDetailViewResponse.Details, 'issues'>>,
): NamedElementEditableDetails => ({
  ...details,
  namedElement: details.namedElement
    ? {
        ...details.namedElement,
        namedElement: details.namedElement.namedElement
          ? namedElementConfig(details.namedElement.namedElement)
          : null,
      }
    : null,
});

export const suggestionToEditableDetails = (
  suggestion: Suggestion,
): NamedElementEditableDetails => ({
  silence: null,
  watched: false,
  ignoreDeadClicks: false,
  ignoreRageClicks: false,
  namedElement: {
    namedElement: {
      ...suggestion,
      id: '',
      platformSelectors: suggestion.platformSelectors.map(ps => ({
        ...ps,
        originalSelector: ps.selector,
        canonicalizedSelector: null,
      })),
      apiName: '',
      suggestion,
    },
    metadata: null,
    createIdempotenceKey: '',
  },
});

export const namedElementConfig = (namedElement: NamedElement): NamedElementConfig => ({
  ...namedElement,
  platformSelectors: namedElement.platformSelectors.map(ps => ({
    ...ps,
    originalSelector: ps.selector,
    canonicalizedSelector: null,
  })),
});

export const getNamedElementMetadata = (namedElement: NamedElementRecord): Metadata => ({
  createdBy: namedElement?.metadata?.createdBy || '',
  createdAt: namedElement?.metadata?.created || null,
  updatedBy: namedElement?.metadata?.lastUpdatedBy || '',
  updatedAt: namedElement?.metadata?.lastUpdated || null,
});

export const getNamedElementName = (namedElement: NamedElementRecord) =>
  namedElement.namedElement?.name || '';

export const placeholderElementPropertyRuleRecord = (
  partial: Partial<ElementPropertyRule> = {},
): ElementPropertyRuleRecord => ({
  elementPropertyRule: {
    id: '',
    name: '',
    description: '',
    propertyType: vars.Type.INVALID_TYPE,
    propertyName: '',
    ...partial,
  },
  metadata: {
    lastUpdated: null,
    lastUpdatedBy: '',
    created: null,
    createdBy: '',
    archived: false,
    visibility: Visibility.UNSET,
    version: int64.nil,
    sequentialVersion: int64.nil,
  },
});

export const defaultNewElementPropertyRuleRecord = (
  _partial: Pick<ElementPropertyRule, 'namedElementId'> = {},
): ElementPropertyRuleRecord =>
  placeholderElementPropertyRuleRecord({ ..._partial, innerText: {} });

const flattenElementPropertyRule = (
  elementPropertyRule: ElementPropertyRuleRecord,
): FlattenedElementPropertyRule | null =>
  elementPropertyRule.elementPropertyRule
    ? {
        ...elementPropertyRule.elementPropertyRule,
        lastUpdated: elementPropertyRule.metadata?.lastUpdated || null,
        lastUpdatedBy: elementPropertyRule.metadata?.lastUpdatedBy || '',
        archived: elementPropertyRule.metadata?.archived || false,
        createdBy: elementPropertyRule.metadata?.createdBy || '',
      }
    : null;

export const flattenElementPropertyRules = (
  elementPropertyRules: ElementPropertyRuleRecord[],
): FlattenedElementPropertyRule[] =>
  elementPropertyRules
    .map(elementPropertyRule => flattenElementPropertyRule(elementPropertyRule))
    .filter(isNonNull);

export const elementPropertyRuleConfig = (
  elementPropertyRule: ElementPropertyRule,
  elementPropertyRulesV2Enabled: boolean = false,
): ElementPropertyRuleConfig => {
  const { selectors, childSelectors, ...noSelectors } = elementPropertyRule;
  const config: ElementPropertyRuleConfig = noSelectors;
  if (selectors) {
    config.selectors = {
      selectors: selectors.selectors.map(selector => ({
        ...selector,
        originalSelector: selector.selector,
        canonicalizedSelector: null,
      })),
    };
  }
  if (!config.any && !config.namedElementId && !config.selectors) {
    config.namedElementId = ''; // prefer initializing with named element select
  }
  if (childSelectors) {
    config.childSelectors = {
      selectors: childSelectors.selectors.map(selector => ({
        ...selector,
        originalSelector: selector.selector,
        canonicalizedSelector: null,
      })),
    };
  }
  // if the name and propertyName already match set to true, otherwise default to true.
  if (elementPropertyRulesV2Enabled) {
    config.usePropertyName = config.name === config.propertyName ?? true;
  }
  return config;
};

export const getMetadata = (metadata?: AuditMetadata | null): Metadata => ({
  createdBy: metadata?.createdBy || '',
  createdAt: metadata?.created || null,
  updatedBy: metadata?.lastUpdatedBy || '',
  updatedAt: metadata?.lastUpdated || null,
});

export const getUnionDepsForSelectors = (selectors: PlatformSelector[] | undefined) => {
  const deps = (selectors ?? []).map(makePlatformSelectorDeps);
  const intersectionDeps = deps
    .filter(deps => deps.length)
    .map(deps => ({ dependentSet: { intersection: { deps } } }));
  // Provide a single union of all selectors for finding a preview.
  // The active selection is always the named element / child selector in the context of the named element.
  const depsUnionForPreview = intersectionDeps.length
    ? [{ dependentSet: { union: { deps: intersectionDeps } } }]
    : [];
  return depsUnionForPreview;
};

export const makePlatformSelectorDeps = (
  selector: PlatformSelector | undefined,
): DependentEventFilter[] => {
  if (!selector || !SelectorRule.isValid(selector?.selector ?? '')) {
    return [];
  }
  return [
    { css: { matches: { value: [selector.selector] } } },
    { nativePlatform: { equals: { value: selector.platforms } } },
  ];
};

export const isPendingSuggestion = (element: NamedElement | null | undefined): boolean =>
  !element?.id &&
  !!element?.suggestion &&
  element?.suggestion?.status === Suggestion.Status.STATUS_PENDING;

function hasSupportedClassOrIdMatch(rule: SelectorPart, attribute: 'class' | 'id'): boolean {
  const operator = rule?.attrMatch?.[attribute];
  return !operator || SUPPORTED_OPERATORS.includes(operator);
}

function hasNthChildPseudoClass(rule: SelectorPart): boolean {
  return rule?.pseudoClasses?.some(
    (pseudoClass: SelectorPseudoClass) => pseudoClass.name === 'nth-child',
  );
}

export function isSupportedSelectorForSearchAndAnalytics(selector: string): boolean {
  const selectorRule = new SelectorRule(selector);
  const rule = selectorRule.selector.rule;

  // Invalid selectors are considered unsupported
  if (!rule) {
    return false;
  }

  return (
    hasSupportedClassOrIdMatch(rule, 'class') &&
    hasSupportedClassOrIdMatch(rule, 'id') &&
    !hasNthChildPseudoClass(rule)
  );
}
